import { Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { resolveImg } from "@/lib/api";

export default function ServiceBreakout({ services, section, id, overline, title, intro, onBook, variant = "light", bgImage }) {
  const items = services.filter((s) => s.section === section);
  if (!items.length) return null;

  const grey = variant === "grey";
  const sectionCls = grey ? "bg-upland-grey text-upland-creme" : "bg-upland-offwhite text-upland-green";
  const overlineCls = grey ? "text-upland-creme/70" : "text-upland-brown";
  const introCls = grey ? "text-upland-creme/90" : "text-upland-grey";

  return (
    <section id={id} data-testid={`${section}-section`} className={`relative overflow-hidden ${sectionCls} py-24 md:py-36`}>
      {bgImage && (
        <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true">
          <img src={resolveImg(bgImage)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-upland-grey/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-upland-grey via-upland-grey/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-upland-grey/70 via-transparent to-upland-grey" />
        </div>
      )}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className={`text-xs uppercase tracking-[0.3em] mb-5 ${overlineCls}`}>{overline}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight max-w-3xl leading-tight">{title}</h2>
          {intro && <p className={`mt-6 max-w-xl text-lg leading-relaxed ${introCls}`}>{intro}</p>}
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <Reveal key={t.id} delay={(i % 3) * 0.1}>
              <div data-testid={`${section}-card-${i}`} className="h-full bg-upland-creme text-upland-green p-8 flex flex-col border-t-4 border-upland-brown">
                <h3 className="font-serif text-2xl md:text-3xl">{t.title}</h3>
                {t.tagline && <p className="text-upland-brown text-sm tracking-wide mt-1">{t.tagline}</p>}
                {t.description && <p className="text-upland-grey mt-4 leading-relaxed flex-1">{t.description}</p>}
                {t.bullets?.length > 0 && (
                  <ul className="mt-6 space-y-2.5">
                    {t.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-2.5 text-sm">
                        <Check size={16} className="mt-0.5 text-upland-green shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        {onBook && (
          <Reveal delay={0.15} className="mt-12">
            <button data-testid={`${section}-cta-btn`} onClick={onBook}
              className={`px-10 py-4 transition-colors ${grey ? "bg-upland-creme text-upland-green hover:bg-white" : "bg-upland-green text-upland-creme hover:bg-upland-brown"}`}>
              Book a Consultation
            </button>
          </Reveal>
        )}
      </div>
    </section>
  );
}
